import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export interface HallOption {
  label: string;
  value: string;
}

export const STATUS_FILTERS = ["all", "Pending Draft", "Approved", "Rejected", "In Process"] as const;

const STATUS_MATCH: Record<string, string[]> = {
  "pending draft": ["pending draft", "draft", "n"],
  approved: ["approved", "approve", "a"],
  rejected: ["rejected", "reject", "r"],
  "in process": [
    "in process",
    "inprocess",
    "to be approved",
    "partially approved",
    "to be processed",
    "pending",
    "t",
    "p",
    "b",
  ],
};

const STATUS_BADGE: Record<string, { label: string; badge: string }> = {
  APPROVED: { label: "Approved", badge: "badge bg-success" },
  APPROVE: { label: "Approved", badge: "badge bg-success" },
  A: { label: "Approved", badge: "badge bg-success" },
  REJECTED: { label: "Rejected", badge: "badge bg-danger" },
  REJECT: { label: "Rejected", badge: "badge bg-danger" },
  R: { label: "Rejected", badge: "badge bg-danger" },
  PENDING_DRAFT: { label: "Pending Draft", badge: "badge bg-secondary" },
  DRAFT: { label: "Draft", badge: "badge bg-secondary" },
  N: { label: "New", badge: "badge bg-primary" },
  PENDING: { label: "Pending", badge: "badge bg-warning text-dark" },
  IN_PROCESS: { label: "In Process", badge: "badge bg-warning text-dark" },
  INPROCESS: { label: "In Process", badge: "badge bg-warning text-dark" },
  TO_BE_APPROVED: { label: "To Be Approved", badge: "badge bg-warning text-dark" },
  T: { label: "To Be Approved", badge: "badge bg-warning text-dark" },
  PARTIALLY_APPROVED: { label: "Partially Approved", badge: "badge bg-warning text-dark" },
  P: { label: "Partially Approved", badge: "badge bg-warning text-dark" },
  TO_BE_PROCESSED: { label: "To Be Processed", badge: "badge bg-info" },
  B: { label: "To Be Processed", badge: "badge bg-info" },
  PROCESSING_COMPLETE: { label: "Processing Complete", badge: "badge bg-info" },
  D: { label: "Processing Complete", badge: "badge bg-info" },
  CANCELLED: { label: "Cancelled", badge: "badge bg-danger" },
  K: { label: "Cancelled", badge: "badge bg-danger" },
};

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(user?.details?.empId || user?.empId || user?.usrid || user?.emp_id || "").trim();
}

function collectLayers(res: any): any[] {
  return [res, res?.data, res?.data?.data, res?.data?.data?.data].filter(
    (layer) => layer !== undefined && layer !== null
  );
}

export function unwrapResponse(res: any): { ok: boolean; message: string; payload: any } {
  for (const layer of collectLayers(res)) {
    if (!layer || typeof layer !== "object" || Array.isArray(layer)) continue;
    const statusOk = layer.status === 200 || layer.status === 201 || layer.success === true;
    if (statusOk) {
      return {
        ok: true,
        message: String(layer.message || ""),
        payload: layer.data !== undefined ? layer.data : layer,
      };
    }
    if (layer.success === false) {
      return { ok: false, message: String(layer.message || layer.ErrorMsg || ""), payload: layer.data ?? layer };
    }
  }
  return {
    ok: false,
    message: String(res?.data?.message || res?.message || ""),
    payload: res?.data?.data ?? res?.data ?? null,
  };
}

export function extractItems(payload: any): any[] {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export function normalizeOptions(payload: any): HallOption[] {
  const dataList = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.data?.data)
        ? payload.data.data
        : [];
  return dataList
    .map((item: any) => ({
      label: String(item?.Ddtext ?? item?.description ?? item?.label ?? item?.Value ?? ""),
      value: String(item?.Value ?? item?.code ?? item?.value ?? ""),
    }))
    .filter((item: HallOption) => item.value);
}

export function resolveOptionLabel(options: HallOption[], code: string): string {
  if (!code || code === "-") return code || "-";
  const found = options.find((option) => option.value === String(code));
  return found?.label || code;
}

function normalizeStatusKey(status: string): string {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

export function statusMatches(requestStatus: string, selected: string): boolean {
  if (!selected || selected === "all") return true;
  const current = normalizeStatusKey(requestStatus);
  const wanted = normalizeStatusKey(selected);
  if (current === wanted) return true;
  return (STATUS_MATCH[wanted] || []).includes(current);
}

export function getRequestStatusUi(raw: any): { label: string; badge: string } {
  const code = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (!code) return { label: "-", badge: "badge bg-secondary" };
  const found = STATUS_BADGE[code];
  if (found) return found;
  if (code.includes("APPROV")) return STATUS_BADGE.APPROVED;
  if (code.includes("REJECT")) return STATUS_BADGE.REJECTED;
  if (code.includes("DRAFT")) return STATUS_BADGE.PENDING_DRAFT;
  if (code.includes("PROCESS") || code.includes("PENDING")) return STATUS_BADGE.IN_PROCESS;
  return { label: String(raw), badge: "badge bg-secondary" };
}

export function canDownloadOrCancel(status: string): boolean {
  const code = String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return code === "APPROVED" || code === "PROCESSING_COMPLETE";
}

export function fieldOf(source: any, ...keys: string[]): string {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") return String(value);
  }
  return "";
}

export function formatDateToDMY(value: any): string {
  if (!value) return "-";
  const text = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}/.test(text)) return text.slice(0, 10);
  if (/^\d{2}\.\d{2}\.\d{4}/.test(text)) {
    const [day, month, year] = text.slice(0, 10).split(".");
    return `${day}-${month}-${year}`;
  }
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text || "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
}

export function parseDmy(value: string): Date | null {
  const match = String(value || "").match(/^(\d{2})[-.](\d{2})[-.](\d{4})/);
  if (!match) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
}

export function toDotFormat(ymd: string): string {
  const [year, month, day] = String(ymd || "").split("-");
  if (!year || !month || !day) return "";
  return `${day}.${month}.${year}`;
}

export function toMidnightIso(ymd: string): string {
  return `${ymd}T00:00:00`;
}

export function todayYmd(): string {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

export function mapListRecord(item: any) {
  return {
    raw: item,
    id: item?.ReferenceNumber ?? item?.referenceNumber ?? item?.id ?? "-",
    personalNumber: item?.EmpID ?? item?.empId ?? "-",
    requestTypeCode: String(item?.RequestType ?? item?.requestType ?? "-"),
    staffQuarterCode: String(item?.StaffQuarter ?? item?.staffQuarter ?? "-"),
    requestCreationDate: formatDateToDMY(item?.RequestCreationDate),
    requestStatus: String(item?.RequestStatus ?? "-"),
    approverId: item?.ApproverUserID ?? "-",
  };
}

export function mapOverviewRecord(item: any) {
  return {
    personnelNo: item?.EmpID ?? "-",
    employeeName: item?.EmployeeName ?? "-",
    requestTypeCode: String(item?.RequestType ?? ""),
    staffQuarterCode: String(item?.StaffQuarter ?? ""),
    startDate: item?.BookingBeginDate ?? "-",
    endDate: item?.BookingEndDate ?? "-",
    reason: item?.Purpose ?? "-",
    requestStatus: String(item?.RequestStatus ?? "-"),
  };
}

export function isHallBookingWorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
}): boolean {
  const type = String(task?.type || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (type === "HALL_BOOKING" || type === "COMMUNITY_HALL" || type === "HALLBOOKING") return true;
  const text = `${task?.taskText || ""} ${task?.workItemText || ""}`.toLowerCase();
  return text.includes("community hall") || text.includes("common room") || text.includes("hall booking");
}

export async function fetchMasterOptions(): Promise<{
  requestTypes: HallOption[];
  staffQuarters: HallOption[];
}> {
  const [requestTypeRes, staffQuarterRes] = await Promise.all([
    Api.post(API_URLS.COMMUNITY_HALL.REQUEST_TYPE_OPTIONS, {}),
    Api.post(API_URLS.COMMUNITY_HALL.STAFF_QUARTER_OPTIONS, {}),
  ]);
  const requestType = unwrapResponse(requestTypeRes);
  const staffQuarter = unwrapResponse(staffQuarterRes);
  return {
    requestTypes: requestType.ok ? normalizeOptions(requestType.payload) : [],
    staffQuarters: staffQuarter.ok ? normalizeOptions(staffQuarter.payload) : [],
  };
}

export async function fetchHallBookings(empId: string, page = 1, limit = 10) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.FIND_ALL, { empId, page, limit });
  const result = unwrapResponse(res);
  if (!result.ok) {
    throw new Error(result.message || "Something went wrong");
  }
  const items = extractItems(result.payload);
  const totalPages = Number(result.payload?.totalPages);
  return {
    items,
    hasMore: Number.isFinite(totalPages) && totalPages > 0 ? page < totalPages : items.length === limit,
  };
}

export async function syncHallBookings(empId: string) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.SYNC, { empId });
  return unwrapResponse(res);
}

export async function checkHallAmount(payload: {
  EmpID: string;
  BookingBeginDate: string;
  BookingEndDate: string;
  RequestType: string;
}) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.CHECK_AMOUNT, payload);
  const result = unwrapResponse(res);
  const nested = result.payload?.data ?? result.payload;
  if (!result.ok || nested?.success === false || nested?.ErrorMsg) {
    return {
      ok: false,
      message: String(nested?.ErrorMsg || result.message || "Something went wrong"),
      data: null,
    };
  }
  return {
    ok: true,
    message: result.message,
    data: {
      BookingAmount: nested?.BookingAmount ?? nested?.bookingAmount ?? "0.00",
      GstAmount: nested?.GstAmount ?? nested?.gstAmount ?? "0.00",
      TotalAmount: nested?.TotalAmount ?? nested?.totalAmount ?? "0.00",
      TotalDays: nested?.TotalDays ?? nested?.totalDays ?? "0.00",
    },
  };
}

export async function createHallBooking(payload: Record<string, string>) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.CREATE_OR_UPDATE, payload);
  return unwrapResponse(res);
}

export async function findOneHallBooking(id: string) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.FIND_ONE, { id });
  const result = unwrapResponse(res);
  if (!result.ok) return null;
  const payload = result.payload;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data) && !payload.ReferenceNumber) {
      return payload.data;
    }
    return payload;
  }
  return null;
}

export async function downloadApprovalLetter(referenceNumber: string) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.DOWNLOAD_APPROVAL_LETTER, {
    ReferenceNumber: referenceNumber,
  });
  const result = unwrapResponse(res);
  const fileData = result.payload?.data ?? result.payload;
  const content = fileData?.Content;
  if (!result.ok || fileData?.ErrorMsg || !content) {
    throw new Error(fileData?.ErrorMsg || result.message || "Something went wrong");
  }
  return {
    fileName: fileData?.FileName || `CommunityHallBooking_${referenceNumber}.pdf`,
    content: String(content),
    extension: fileData?.FileExtension || "pdf",
  };
}

export function saveBase64File(content: string, fileName: string) {
  let cleaned = content.trim();
  const marker = cleaned.indexOf("base64,");
  if (marker !== -1) cleaned = cleaned.slice(marker + "base64,".length);
  cleaned = cleaned.replace(/\s/g, "");
  const byteChars = atob(cleaned);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function cancelHallBooking(empId: string, aReferenceNumber: string) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.CANCELLATION_CREATE, {
    Action: "C",
    ReferenceNumber: "",
    EmpID: empId,
    AReferenceNumber: aReferenceNumber,
  });
  return unwrapResponse(res);
}

export async function displayHallBookings(beginYmd: string, endYmd: string) {
  const res: any = await Api.post(API_URLS.COMMUNITY_HALL.DISPLAY_SET, {
    BookingBeginDate: toMidnightIso(beginYmd),
    BookingEndDate: toMidnightIso(endYmd),
  });
  const result = unwrapResponse(res);
  if (!result.ok) throw new Error(result.message || "Something went wrong");
  return extractItems(result.payload);
}

export async function approveRejectHallBooking(payload: {
  Action: "A" | "R";
  ReferenceNumber: string;
  EmpID: string;
  ApproverUserID: string;
  HRemarks: string;
  isCancellation: boolean;
}) {
  const url = payload.isCancellation
    ? API_URLS.COMMUNITY_HALL.CANCELLATION_APPROVE_REJECT
    : API_URLS.COMMUNITY_HALL.APPROVE_REJECT;
  const res: any = await Api.post(url, {
    Action: payload.Action,
    ReferenceNumber: payload.ReferenceNumber,
    EmpID: payload.EmpID,
    ApproverUserID: payload.ApproverUserID,
    HRemarks: payload.HRemarks,
  });
  return unwrapResponse(res);
}
